<script setup lang="ts">
import { ref } from "vue";
import Button from "../../components/Button.vue";
import OKCancelModal from "../../components/OKCancelModal.vue";
import TextArea from "../../components/TextArea.vue";
import { useModalStore } from "../../stores/modalStore.ts";

const props = withDefaults(
  defineProps<{
    title?: string;
    initialRequestBody: string;
    requestBodySource?: string;
    onRunRequestBody: (requestBodyText: string) => Promise<void>;
  }>(),
  {
    title: "Run Custom Redistribution",
  }
);

const modalStore = useModalStore();
const requestBodyText = ref(props.initialRequestBody);
const runError = ref<string | undefined>(undefined);
const isRunning = ref(false);
const copyStatus = ref<"idle" | "copied" | "error">("idle");

function setCopyStatus(status: "copied" | "error"): void {
  copyStatus.value = status;
  globalThis.setTimeout(() => {
    copyStatus.value = "idle";
  }, 1800);
}

async function copyRequestBody(): Promise<void> {
  try {
    if (typeof navigator.clipboard.writeText === "function") {
      await navigator.clipboard.writeText(requestBodyText.value);
      setCopyStatus("copied");
      return;
    }

    setCopyStatus("error");
  } catch {
    setCopyStatus("error");
  }
}

function handleCancel(): void {
  if (isRunning.value) {
    return;
  }

  modalStore.close();
}

async function handleRunRequestBody(): Promise<void> {
  if (isRunning.value) {
    return;
  }

  isRunning.value = true;
  runError.value = undefined;

  try {
    await props.onRunRequestBody(requestBodyText.value);
    modalStore.close();
  } catch (error) {
    runError.value =
      error instanceof Error
        ? error.message
        : "Failed to run redistribution with the provided request body.";
  } finally {
    isRunning.value = false;
  }
}
</script>

<template>
  <OKCancelModal :title="title">
    <div class="custom-request-modal-layout">
      <div class="custom-request-modal-toolbar">
        <span
          v-if="copyStatus === 'copied'"
          class="custom-request-copy-feedback"
        >
          Copied
        </span>
        <span
          v-else-if="copyStatus === 'error'"
          class="custom-request-copy-feedback custom-request-copy-feedback-error"
        >
          Copy failed
        </span>
        <Button variant="secondary" @click="void copyRequestBody()">
          Copy Request
        </Button>
      </div>

      <p class="custom-request-modal-description">
        Edit the redistribution request body before running the algorithm.
      </p>

      <div class="custom-request-modal-textarea-wrap">
        <TextArea
          v-model="requestBodyText"
          placeholder="Paste or edit JSON request body"
          label="Custom request body"
          :rows="18"
        />
      </div>

      <p v-if="props.requestBodySource" class="custom-request-modal-source">
        {{ props.requestBodySource }}
      </p>

      <p v-if="runError" class="custom-request-modal-error" role="alert">
        {{ runError }}
      </p>

      <div class="custom-request-modal-actions">
        <Button variant="secondary" :disabled="isRunning" @click="handleCancel">
          Cancel
        </Button>
        <Button
          variant="primary"
          :disabled="isRunning"
          @click="void handleRunRequestBody()"
        >
          {{ isRunning ? "Running..." : "Run Redistribution Algorithm" }}
        </Button>
      </div>
    </div>
  </OKCancelModal>
</template>

<style scoped>
.custom-request-modal-layout {
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 0.75rem;
}

.custom-request-modal-toolbar {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
}

.custom-request-copy-feedback {
  color: #15803d;
  font-size: 0.75rem;
  font-weight: 600;
}

.custom-request-copy-feedback-error {
  color: #b91c1c;
}

.custom-request-modal-description {
  margin: 0;
  color: #475569;
  font-size: 0.9rem;
}

.custom-request-modal-source {
  margin: 0;
  color: #64748b;
  font-size: 0.8rem;
  font-style: italic;
}

.custom-request-modal-textarea-wrap {
  display: flex;
  min-height: 12rem;
  flex: 1;
}

/* stylelint-disable-next-line selector-pseudo-class-no-unknown */
.custom-request-modal-textarea-wrap :deep(textarea.textbox) {
  height: 100%;
  min-height: 12rem;
  resize: both;
}

.custom-request-modal-error {
  margin: 0;
  border-radius: 0.5rem;
  border: 1px solid #ef4444;
  background-color: #fef2f2;
  color: #b91c1c;
  font-size: 0.85rem;
  padding: 0.5rem 0.75rem;
}

.custom-request-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: auto;
}
</style>
